import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../state';
import { CartController } from './CartController';
import { MobileCartView } from './MobileCartView';
import { DesktopCartView } from './DesktopCartView';

interface CartViewProps {
  isMobile?: boolean;
}

const ANIMATION_DURATION = {
  ADD: 400,
  REMOVE: 300
};

export const CartView: React.FC<CartViewProps> = observer(({ isMobile = false }) => {
  const rootStore = useStore();
  const { cartStore } = rootStore;
  const controller = useMemo(() => new CartController(rootStore), [rootStore]);
  const [animatingItems, setAnimatingItems] = useState<Map<string, 'add' | 'remove'>>(new Map());
  const prevItemCountRef = useRef(0);

  const handleRemoveItem = (lineId: string) => {
    setAnimatingItems(prev => new Map(prev).set(lineId, 'remove'));
    setTimeout(() => {
      controller.removeFromCart(lineId);
    }, ANIMATION_DURATION.REMOVE);
  };

  useEffect(() => {
    const styleId = 'cart-item-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes cartItemPopIn {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes cartItemPopOut {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.8); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    controller.fetchCart();
  }, [controller]);

  useEffect(() => {
    const currentCount = cartStore.items.length;
    const prevCount = prevItemCountRef.current;

    if (currentCount > prevCount) {
      const newItem = cartStore.items[currentCount - 1];
      if (newItem) {
        setAnimatingItems(prev => new Map(prev).set(newItem.id, 'add'));
        setTimeout(() => {
          setAnimatingItems(prev => {
            const next = new Map(prev);
            next.delete(newItem.id);
            return next;
          });
        }, ANIMATION_DURATION.ADD);
      }
    }

    prevItemCountRef.current = currentCount;
  }, [cartStore.items.length]);

  const commonProps = {
    cartStore,
    controller,
    animatingItems,
    animationDuration: ANIMATION_DURATION,
    onRemoveItem: handleRemoveItem
  };

  return isMobile ? <MobileCartView {...commonProps} /> : <DesktopCartView {...commonProps} />;
});
